import {isNotNil} from 'ramda';

import {useRowClipboard} from '../../hooks/useRowClipboard';
import type {EagGridStore} from '../../store/createEagGridStore';
import {EagGridStoreContext} from '../../store/EagGridStoreContext';
import {useClipboardExportAllowed} from '../../store/selectors/useBehavior';
import {useCanCopyColumn} from '../../store/selectors/useCanCopyColumn';
import type {EagColumn} from '../../types/EagColumn';
import {copyColumnToClipboard} from '../../utils/copyColumnToClipboard';
import {
  RowContextMenuContent,
  type RowContextMenuContentProps,
} from '../RowContextMenu/RowContextMenuContent';

type LiveRowContextMenuProps = Omit<
  RowContextMenuContentProps,
  'clipboardEnabled' | 'onCopyRow' | 'isRowCopyDisabled' | 'onCopyColumn' | 'isColumnCopyDisabled'
> & {
  store: EagGridStore;
  rowId: string;
  columnKey: string | null;
  columns: EagColumn[];
};

function SubscribedRowContextMenu(props: LiveRowContextMenuProps) {
  const clipboardEnabled = useClipboardExportAllowed();
  const {canCopyRow, copyRow} = useRowClipboard(props.rowId, props.columns);
  const column = props.columns.find((candidate) => candidate.key === props.columnKey);
  const canCopyColumn = useCanCopyColumn(props.columnKey ?? '');

  return (
    <RowContextMenuContent
      {...props}
      clipboardEnabled={clipboardEnabled}
      onCopyRow={copyRow}
      isRowCopyDisabled={!canCopyRow}
      onCopyColumn={
        isNotNil(column)
          ? () => {
              void copyColumnToClipboard(props.store, column);
            }
          : null
      }
      isColumnCopyDisabled={!canCopyColumn}
    />
  );
}

/** openDropdown mounts outside the grid; restore its store and subscribe inside that mounted tree. */
export function LiveRowContextMenu(props: LiveRowContextMenuProps) {
  return (
    <EagGridStoreContext.Provider value={props.store}>
      <SubscribedRowContextMenu {...props} />
    </EagGridStoreContext.Provider>
  );
}
